import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView, Alert, ActivityIndicator, Linking, Platform } from 'react-native';
import { Button, Card, Text, useTheme, Portal, Modal, IconButton } from 'react-native-paper';
import { supabase } from '../services/supabase';
import axios from 'axios';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';

type Empresa = {
  id: number;
  nome: string;
  hostapi: string | null;
  rota: string | null;
  key: string | null;
};

type Automacao = {
  id: number;
  codempresa: number;
  idchatbot: number;
  nomeautomacao: string;
  agendamento: string | null;
  status: string | null;
  dataultatualizacao: string;
  relatorio: string | null;
  ultimolog: any | null;
  webhook_n8n: string;
  filename: string | null;
  empresa?: Empresa;
  chatbot?: {
    id: number;
    instance_name: string;
  };
};

export function Automacoes() {
  const [automacoes, setAutomacoes] = useState<Automacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executingId, setExecutingId] = useState<number | null>(null);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [selectedCodigo, setSelectedCodigo] = useState<string>('');
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const theme = useTheme();

  const fetchAutomacoes = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('empresa')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.empresa) throw new Error('Empresa não encontrada');

      const { data, error } = await supabase
        .from('chatbot_automacoes')
        .select(`
          *,
          empresa:codempresa(
            id,
            nome,
            hostapi,
            rota,
            key
          ),
          chatbot:idchatbot(
            id,
            instance_name
          )
        `)
        .eq('codempresa', userData.empresa)
        .order('dataultatualizacao', { ascending: false });

      if (error) throw error;
      setAutomacoes(data || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Erro ao buscar automações:', err);
    } finally {
      setLoading(false);
    }
  };

  const executarAutomacao = async (automacao: Automacao) => {
    try {
      setExecutingId(automacao.id);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Não foi possível obter o token de acesso');

      const headers = {
        'authorization': `Bearer ${process.env.N8N_AUTHORIZATION_TOKEN || ''}`,
        'apikey': session.access_token,
        'instancename': automacao.chatbot?.instance_name || '',
      };

      const body = {
        ROTAAPI: automacao.empresa?.rota || '',
        RELATORIO: automacao.relatorio || '',
        DESCRICAO: automacao.nomeautomacao,
        FILENAME: automacao.filename || ''
      };

      // Envia a requisição para o webhook
      const n8nBaseUrl = process.env.N8N_BASE_URL || 'https://n8n.smartbr.app';
      const response = await axios.post(`${n8nBaseUrl}/${automacao.webhook_n8n}`, body, { headers });
      console.log('Resposta do webhook:', response.data);

      // Se a resposta for um array (sucesso imediato)
      if (Array.isArray(response.data)) {
        await supabase
          .from('chatbot_automacoes')
          .update({ 
            dataultatualizacao: new Date().toISOString(),
            status: 'OK',
            ultimolog: response.data
          })
          .eq('id', automacao.id);
          
        Alert.alert('Execução concluída', `Automação "${automacao.nomeautomacao}" foi executada com sucesso!`);
      } 
      // Se a resposta contiver uma mensagem de erro
      else if (response.data && response.data.message === "Error in workflow") {
        await supabase
          .from('chatbot_automacoes')
          .update({ 
            dataultatualizacao: new Date().toISOString(),
            status: 'Erro',
            ultimolog: response.data
          })
          .eq('id', automacao.id);
          
        Alert.alert('Erro na execução', `Ocorreu um erro ao executar a automação "${automacao.nomeautomacao}".`);
      }
      // Caso contrário, marca como "Executando" e inicia a verificação periódica
      else {
        await supabase
          .from('chatbot_automacoes')
          .update({ 
            dataultatualizacao: new Date().toISOString(),
            status: 'Executando',
            ultimolog: { message: 'Execução iniciada', timestamp: new Date().toISOString() }
          })
          .eq('id', automacao.id);
        
        // Inicia verificação imediata
        setTimeout(() => {
          verificarStatusExecucao(automacao.id, automacao.webhook_n8n);
        }, 5000);
        
        Alert.alert('Execução iniciada', `Automação "${automacao.nomeautomacao}" iniciada com sucesso! A execução pode levar alguns minutos para ser concluída.`);
      }

      await fetchAutomacoes();
    } catch (err: any) {
      console.error('Erro ao executar automação:', err);
      
      // Atualiza com status de erro caso falhe a execução inicial
      await supabase
        .from('chatbot_automacoes')
        .update({ 
          dataultatualizacao: new Date().toISOString(),
          status: 'Erro',
          ultimolog: { error: err.message, details: err.response?.data || 'Erro ao iniciar execução' }
        })
        .eq('id', automacao.id);
        
      setError('Erro ao executar automação: ' + err.message);
    } finally {
      setExecutingId(null);
    }
  };

  const showLog = (automacao: Automacao) => {
    setSelectedLog(automacao.ultimolog);
    setShowRawJson(false);
    setLogModalVisible(true);
  };

  const visualizarDocumento = (documento: string, codigo: string) => {
    try {
      // Verifica se a string base64 já tem o prefixo
      const base64Data = documento.startsWith('data:application/pdf;base64,') 
        ? documento 
        : `data:application/pdf;base64,${documento}`;
      
      setSelectedDocument(base64Data);
      setSelectedCodigo(codigo);
      setDocumentModalVisible(true);
    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      Alert.alert(
        'Erro',
        'Ocorreu um erro ao tentar visualizar o documento.',
        [{ text: 'OK' }]
      );
    }
  };

  const renderPdfBase64 = (base64String: string) => {
    // Esta função não é mais necessária, mas mantemos para referência futura
    return '';
  };

  // Função para verificar o status da execução
  const verificarStatusExecucao = async (automacaoId: number, webhook: string) => {
    try {
      console.log('Verificando status da execução:', automacaoId, webhook);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.log('Sem token de acesso, não é possível verificar o status');
        return;
      }

      const headers = {
        'authorization': 'Bearer 8hF4DpG2v3JbT6sQ7kZ5wR1yU0cM9xN8',
        'apikey': session.access_token,
      };

      // Busca a automação atual
      const { data: automacaoAtual } = await supabase
        .from('chatbot_automacoes')
        .select('*')
        .eq('id', automacaoId)
        .single();

      console.log('Automação atual:', automacaoAtual);

      // Se o status não for "Executando", não precisa verificar
      if (!automacaoAtual || automacaoAtual.status !== 'Executando') {
        console.log('Automação não está em execução, parando verificação');
        if (statusCheckInterval) {
          clearInterval(statusCheckInterval);
          setStatusCheckInterval(null);
        }
        return;
      }

      try {
        console.log('Enviando requisição para verificar status:', `https://n8n.smartbr.app/${webhook}`);
        
        // Tenta obter o resultado da execução diretamente
        const resultResponse = await axios.post(`https://n8n.smartbr.app/${webhook}`, {}, { headers });
        
        console.log('Resposta da verificação:', resultResponse.data);
        
        // Verifica se a resposta contém um erro
        if (resultResponse.data && resultResponse.data.message === "Error in workflow") {
          console.log('Erro na execução detectado');
          
          // Atualiza com o erro
          await supabase
            .from('chatbot_automacoes')
            .update({ 
              dataultatualizacao: new Date().toISOString(),
              status: 'Erro',
              ultimolog: resultResponse.data
            })
            .eq('id', automacaoId);
          
          // Para de verificar o status
          if (statusCheckInterval) {
            clearInterval(statusCheckInterval);
            setStatusCheckInterval(null);
          }
          
          // Atualiza a lista de automações
          await fetchAutomacoes();
        } 
        // Verifica se a resposta é um array (sucesso)
        else if (Array.isArray(resultResponse.data)) {
          console.log('Execução concluída com sucesso');
          
          // Atualiza com o resultado completo
          await supabase
            .from('chatbot_automacoes')
            .update({ 
              dataultatualizacao: new Date().toISOString(),
              status: 'OK',
              ultimolog: resultResponse.data
            })
            .eq('id', automacaoId);
          
          // Para de verificar o status
          if (statusCheckInterval) {
            clearInterval(statusCheckInterval);
            setStatusCheckInterval(null);
          }
          
          // Atualiza a lista de automações
          await fetchAutomacoes();
        } else {
          console.log('Resposta não reconhecida, continuando verificação');
        }
      } catch (resultErr: any) {
        // Ignora erros de verificação de status, continua tentando
        console.log('Erro ao verificar status:', resultErr.message);
      }
    } catch (err: any) {
      console.error('Erro ao verificar status da execução:', err.message);
    }
  };

  const renderLogAmigavel = (log: any) => {
    if (!log) return <Text>Nenhum log disponível</Text>;
    
    // Se for um erro simples
    if (log.message) {
      return (
        <View style={styles.logItemContainer}>
          <Text style={styles.logItemTitle}>Mensagem:</Text>
          <Text style={styles.logItemValue}>{log.message}</Text>
        </View>
      );
    }
    
    // Se for um array de resultados (caso de sucesso)
    if (Array.isArray(log)) {
      return (
        <View>
          {log.map((item, index) => (
            <Card key={index} style={styles.logCard}>
              <Card.Content>
                <Text style={styles.logItemTitle}>Execução {index + 1}</Text>
                
                <View style={styles.logItemRow}>
                  <View style={styles.logItemField}>
                    <Text style={styles.logItemLabel}>Fluxo:</Text>
                    <Text style={styles.logItemValue}>{item.nomefluxo || '-'}</Text>
                  </View>
                  
                  {item.log && (
                    <>
                      <View style={styles.logItemField}>
                        <Text style={styles.logItemLabel}>Código:</Text>
                        <Text style={styles.logItemValue}>{item.log.codusur || '-'}</Text>
                      </View>

                      <View style={styles.logItemField}>
                        <Text style={styles.logItemLabel}>Nome:</Text>
                        <Text style={styles.logItemValue}>{item.log.nome || '-'}</Text>
                      </View>
                      
                      <View style={styles.logItemField}>
                        <Text style={styles.logItemLabel}>Telefone:</Text>
                        <Text style={styles.logItemValue}>{item.log.phone || '-'}</Text>
                      </View>
                      
                      <View style={styles.logItemField}>
                        <Text style={styles.logItemLabel}>Status:</Text>
                        <Text style={[
                          styles.logItemValue,
                          item.log.error ? styles.logItemError : styles.logItemSuccess
                        ]}>
                          {item.log.error ? 'Erro' : 'OK'}
                        </Text>
                      </View>

                      {item.log.error && item.log.status && (
                        <View style={styles.logItemField}>
                          <Text style={styles.logItemLabel}>Detalhes:</Text>
                          <Text style={[styles.logItemValue, styles.logItemError]}>{item.log.status}</Text>
                        </View>
                      )}
                    </>
                  )}
                </View>
                
                {item.log?.document && (
                  <Button
                    mode="outlined"
                    onPress={() => visualizarDocumento(item.log.document, item.log.codusur.toString())}
                    style={styles.documentButton}
                  >
                    Ver Documento
                  </Button>
                )}
              </Card.Content>
            </Card>
          ))}
        </View>
      );
    }
    
    // Caso seja um objeto simples
    return (
      <View style={styles.logItemContainer}>
        <Text style={styles.logItemTitle}>Resultado:</Text>
        <Text style={styles.logItemValue}>{JSON.stringify(log)}</Text>
      </View>
    );
  };

  useEffect(() => {
    fetchAutomacoes();

    // Limpa o intervalo quando o componente for desmontado
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, []);

  useEffect(() => {
    if (executingId) {
      console.log('Iniciando verificação periódica para automação ID:', executingId);
      
      // Limpa qualquer intervalo existente
      if (statusCheckInterval) {
        console.log('Limpando intervalo existente');
        clearInterval(statusCheckInterval);
      }
      
      // Inicia um novo intervalo
      const intervalId = setInterval(() => {
        console.log('Executando verificação periódica...');
        const automacao = automacoes.find((a) => a.id === executingId);
        if (automacao) {
          console.log('Automação encontrada:', automacao.nomeautomacao);
          verificarStatusExecucao(automacao.id, automacao.webhook_n8n);
        } else {
          console.log('Automação não encontrada, parando verificação');
          clearInterval(intervalId);
          setStatusCheckInterval(null);
        }
      }, 30000); // Verificar a cada 30 segundos
      
      setStatusCheckInterval(intervalId);
      
      // Executa uma verificação imediata
      const automacao = automacoes.find((a) => a.id === executingId);
      if (automacao) {
        console.log('Executando verificação inicial');
        verificarStatusExecucao(automacao.id, automacao.webhook_n8n);
      }
    } else if (statusCheckInterval) {
      console.log('Parando verificação periódica');
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
  }, [executingId, automacoes]);

  if (loading && automacoes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <>
      <ScrollView style={styles.container}>
        {error && (
          <Card style={[styles.card, styles.errorCard]}>
            <Card.Content>
              <Text style={styles.errorText}>{error}</Text>
            </Card.Content>
          </Card>
        )}

        <View style={styles.headerContainer}>
          <Text variant="titleLarge">Automações Configuradas</Text>
          <IconButton
            icon="refresh"
            size={24}
            onPress={fetchAutomacoes}
            disabled={loading}
          />
        </View>

        {automacoes.map((automacao) => (
          <Card key={automacao.id} style={styles.card}>
            <Card.Content>
              <View style={styles.cardHeader}>
                <Text variant="titleMedium" style={styles.automacaoTitle}>
                  {automacao.nomeautomacao}
                </Text>
                {executingId === automacao.id ? (
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                ) : (
                  <Button
                    mode="contained"
                    onPress={() => executarAutomacao(automacao)}
                    style={styles.executeButton}
                  >
                    Executar
                  </Button>
                )}
              </View>

              <View style={styles.infoContainer}>
                <Text>Status: {automacao.status || 'Não executado'}</Text>
                <Text>Última atualização: {new Date(automacao.dataultatualizacao).toLocaleString()}</Text>
                {automacao.agendamento && (
                  <Text>Agendamento: {automacao.agendamento}</Text>
                )}
              </View>

              {automacao.ultimolog && (
                <Button
                  mode="outlined"
                  onPress={() => showLog(automacao)}
                  style={styles.logButton}
                >
                  Visualizar Log
                </Button>
              )}
            </Card.Content>
          </Card>
        ))}
      </ScrollView>

      <Portal>
        <Modal
          visible={logModalVisible}
          onDismiss={() => setLogModalVisible(false)}
          contentContainerStyle={styles.modalContent}
        >
          <ScrollView>
            <Text variant="titleMedium" style={styles.logTitle}>Log da Execução</Text>
            {showRawJson ? (
              <Text style={styles.logText}>
                {selectedLog ? JSON.stringify(selectedLog, null, 2) : 'Nenhum log disponível'}
              </Text>
            ) : (
              renderLogAmigavel(selectedLog)
            )}
            <Button
              mode="outlined"
              onPress={() => setShowRawJson(!showRawJson)}
              style={styles.logButton}
            >
              {showRawJson ? 'Visualização amigável' : 'Exibir JSON bruto'}
            </Button>
          </ScrollView>
          <Button
            mode="contained"
            onPress={() => setLogModalVisible(false)}
            style={styles.closeButton}
          >
            Fechar
          </Button>
        </Modal>
      </Portal>

      <Portal>
        <Modal
          visible={documentModalVisible}
          onDismiss={() => setDocumentModalVisible(false)}
          contentContainerStyle={styles.documentModalContent}
        >
          <Text variant="titleMedium" style={styles.logTitle}>Documento - Código: {selectedCodigo}</Text>
          {selectedDocument && (
            <View style={styles.documentContainer}>
              <iframe
                src={selectedDocument}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={`Documento ${selectedCodigo}`}
              />
            </View>
          )}
          <Button
            mode="contained"
            onPress={() => setDocumentModalVisible(false)}
            style={styles.closeButton}
          >
            Fechar
          </Button>
        </Modal>
      </Portal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  errorCard: {
    backgroundColor: '#ffebee',
  },
  errorText: {
    color: '#c62828',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  automacaoTitle: {
    flex: 1,
    marginRight: 16,
  },
  infoContainer: {
    gap: 4,
  },
  executeButton: {
    minWidth: 100,
  },
  logButton: {
    marginTop: 12,
  },
  modalContent: {
    backgroundColor: 'white',
    margin: 20,
    padding: 20,
    borderRadius: 8,
    maxHeight: '80%',
  },
  logTitle: {
    marginBottom: 12,
  },
  logText: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
  closeButton: {
    marginTop: 16,
  },
  logItemContainer: {
    marginBottom: 12,
  },
  logItemTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  logItemRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
    marginBottom: 8,
  },
  logItemField: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  logItemLabel: {
    fontSize: 14,
    color: '#666',
  },
  logItemValue: {
    fontSize: 14,
  },
  logItemError: {
    color: '#c62828',
  },
  logItemSuccess: {
    color: '#2ecc71',
  },
  logCard: {
    marginBottom: 16,
  },
  documentButton: {
    marginTop: 12,
  },
  documentModalContent: {
    backgroundColor: 'white',
    margin: 20,
    padding: 20,
    borderRadius: 8,
    height: '90%',
    width: '90%',
    alignSelf: 'center',
  },
  documentContainer: {
    flex: 1,
    marginVertical: 16,
    height: '80%',
  },
});
